/**
 * confirmAppointment: 選択された候補日時でアポイントを確定する。
 * US-D2-03、FR-Y1, FR-CAL1, FR-CAL3。BR-U6-2: ログイン検証、BR-U6-5: 業務エラー時の再取得。
 */

import { relayClient } from "../core/relayClient";
import { classifyError, logClientWarning, toFailureKind } from "../core/errorHandler";
import { isLoggedIn } from "../core/authState";
import type { MeetingType, FailureKind } from "../types";

export interface ConfirmAppointmentParams {
  memberGroupId: string;
  scheduledStartAt: string;
  screenType: string;
  objectType: string;
  objectRootId: string;
  scenarioId?: string;
  /** 商談形式（2026-08-04対面商談対応〔U12〕BR-U6-12により追加、必須）。 */
  meetingType: MeetingType;
  /** ご要望・メッセージ（任意、ラベルはmeetingTypeで分岐。2026-08-04追加）。 */
  message?: string;
  /** ご訪問希望場所（meetingType=ON_SITEの場合のみ、任意。2026-08-04追加）。 */
  visitPreferredLocation?: string;
  /**
   * 特設サイトフラグ（U13 site_type統一Unit、2026-08-04追加、任意）。
   * ContentDisplayResult.prFlgをそのまま渡す。中継エンドポイント（HCAPI）がsite_type/サブID解決の
   * 起点として使用する。
   */
  prFlg?: boolean | null;
  /** レコメンドCV経由のアポイントかどうか（任意、デフォルトfalse）。 */
  isRecommendCv?: boolean;
  /**
   * アンケート回答ログID（survey_response_log.id、任意）。アンケート回答経由でこの導線に進んだ場合のみ
   * `submitSurveyAnswer`の戻り値から引き継いで送信する。アンケートを経由しない導線では渡さない
   * （未指定＝紐付けなし。2026-09-24 BR-U2-21 誤紐付け修正）。
   */
  surveyResponseLogId?: string;
}

export interface ConfirmAppointmentResult {
  success: boolean;
  requiresLogin: boolean;
  appointmentId?: string;
  scheduledStartAt?: string;
  scheduledEndAt?: string;
  exhibitorCompanyName?: string | null;
  slotUnavailable: boolean;
  /**
   * 同一企業への重複アポイント（BR-U2-18）。既存の有効アポイントが存在するため作成が拒否された場合に設定。
   * `existingAppointmentId`はキャンセル導線（「こちら」リンク）に用いる。
   */
  duplicateCompany?: { existingAppointmentId: string };
  /**
   * 効果分析ログの失敗大分類（VALIDATION/SLOT_UNAVAILABLE/DUPLICATE/SYSTEM/AUTH、BR-AN-6）。
   * 失敗時のみ設定。APPOINTMENT_SUBMIT_FAILED イベントの発火に用いる（呼び出し元）。
   */
  failureKind?: FailureKind;
  /** 効果分析ログの業務エラーコード（spica-apiのErrorCode、BR-AN-6）。失敗時に取得できれば設定。 */
  businessErrorCode?: string;
}

/**
 * spica-apiのAppointmentResponse（web/dto/AppointmentResponse.java）に対応。
 * 2026-07-15 M-1申し送り対応: exhibitorCompanyName（出展企業名）をレスポンスに追加済み
 * （CompanyReader経由でcompany_root.nameを解決、取得不可時はnull）。
 */
interface CreateAppointmentData {
  id: string;
  scheduledStartAt: string;
  scheduledEndAt: string;
  exhibitorCompanyName: string | null;
}

export async function confirmAppointment(
  params: ConfirmAppointmentParams
): Promise<ConfirmAppointmentResult> {
  if (!isLoggedIn()) {
    return { success: false, requiresLogin: true, slotUnavailable: false };
  }

  const response = await relayClient.createAppointment<CreateAppointmentData>(params);

  if (response.status === 401) {
    return { success: false, requiresLogin: true, slotUnavailable: false };
  }

  if (!response.ok || !response.data) {
    const classified = classifyError(response);
    const slotUnavailable =
      classified.businessErrorCode === "SLOT_NOT_AVAILABLE" ||
      classified.businessErrorCode === "SLOT_ALREADY_RESERVED";
    // 効果分析ログ（BR-AN-6）: 失敗の大分類・業務エラーコードを返り値へ透過する（呼び出し元が
    // APPOINTMENT_SUBMIT_FAILED イベントに載せる）。既存の slotUnavailable/duplicateCompany は互換維持。
    const failureKind = toFailureKind(classified);
    const businessErrorCode = classified.businessErrorCode;
    logClientWarning(`confirmAppointment failed: ${classified.kind}`);
    if (
      classified.businessErrorCode === "DUPLICATE_COMPANY_APPOINTMENT" &&
      response.existingAppointmentId
    ) {
      return {
        success: false,
        requiresLogin: false,
        slotUnavailable: false,
        duplicateCompany: { existingAppointmentId: response.existingAppointmentId },
        failureKind,
        businessErrorCode,
      };
    }
    return { success: false, requiresLogin: false, slotUnavailable, failureKind, businessErrorCode };
  }

  return {
    success: true,
    requiresLogin: false,
    appointmentId: response.data.id,
    scheduledStartAt: response.data.scheduledStartAt,
    scheduledEndAt: response.data.scheduledEndAt,
    exhibitorCompanyName: response.data.exhibitorCompanyName,
    slotUnavailable: false,
  };
}
